import { Training } from '../training.service';
import { TrainingSessionData} from '../../../components/TrainingComponents/TrainingSession';

/**
 * Adapts a Firebase Training object to the TrainingSessionData format used by UI components
 * @param firebaseTraining Training data from Firebase
 * @returns TrainingSessionData that can be directly used by UI components
 */
export const adaptFirebaseTrainingToSessionData = (
  firebaseTraining: Training
): TrainingSessionData => {
  // Check type mapping to ensure compatibility
  const mappedType = mapTrainingType(firebaseTraining.type);
  
  return {
    id: firebaseTraining.id,
    date: new Date(firebaseTraining.date),
    type: mappedType,
    totalTrainedTime: firebaseTraining.totalTrainedTime,
    rollTime: firebaseTraining.rollTime,
    gi: firebaseTraining.gi,
    trainedStyle: firebaseTraining.trainedStyle,
    location: firebaseTraining.location,
    instructor: firebaseTraining.instructor, // Original format is string uid
    note: firebaseTraining.note, // Use correct field name
    trainingPartners: firebaseTraining.taggedFriends 
      ? Object.entries(firebaseTraining.taggedFriends).map(([id, data]) => ({
          id,
          name: data.username || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unknown',
          avatarUrl: data.profilePhotoURL
        }))
      : [],
    flows: firebaseTraining.flows,
    rounds: firebaseTraining.rounds ? Object.keys(firebaseTraining.rounds).length : 0,
    photoUrl: firebaseTraining.photos 
      ? Object.values(firebaseTraining.photos)[0]?.url 
      : undefined,
    videoUrl: firebaseTraining.videosYT // Use correct field name (Record<string, string>)
      ? `https://www.youtube.com/watch?v=${Object.values(firebaseTraining.videosYT)[0]}`
      : undefined,
    _pendingSync: false
  };
};

/**
 * Adapts training session data from UI back to Firebase format for saving
 * @param sessionData TrainingSessionData from UI components
 * @param existingData Optional existing Firebase training data to merge with
 * @returns Partial<Training> format suitable for Firebase update operations
 */
export const adaptSessionDataToFirebaseTraining = (
  sessionData: TrainingSessionData,
  uid: string,
  existingData?: Partial<Training>
): Partial<Training> => {
  // Start with a base object
  const firebaseTraining: Partial<Training> = {
    ...existingData,
    owner: uid,
    title: sessionData.location?.name || 'Training Session',
    date: typeof sessionData.date === 'string' 
      ? new Date(sessionData.date).getTime() 
      : sessionData.date.getTime(),
    type: mapSessionTypeToFirebase(sessionData.type) as Training['type'],
    totalTrainedTime: sessionData.totalTrainedTime,
    rollTime: sessionData.rollTime,
    gi: sessionData.gi,
    trainedStyle: sessionData.trainedStyle,
    note: sessionData.note, // Use correct field name
    dateModified: Date.now()
  };

  // Add location if specified
  if (sessionData.location) {
    firebaseTraining.location = sessionData.location;
  }

  // Add instructor if specified (original format is string uid)
  if (sessionData.instructor) {
    firebaseTraining.instructor = sessionData.instructor;
  }

  // Add flows if specified
  if (sessionData.flows && sessionData.flows.length > 0) {
    firebaseTraining.flows = sessionData.flows;
  }

  // Add partners if specified
  if (sessionData.trainingPartners && sessionData.trainingPartners.length > 0) {
    const taggedFriends: Record<string, any> = {};
    
    sessionData.trainingPartners.forEach(partner => {
      taggedFriends[partner.id] = {
        username: partner.name,
        profilePhotoURL: partner.avatarUrl,
        firstName: '',
        lastName: '',
        isInstructor: false
      };
    });
    
    firebaseTraining.taggedFriends = taggedFriends;
  }

  return firebaseTraining;
};

/**
 * Maps Firebase training types to UI training types
 * Firebase stores: 'training', 'seminar', 'camp', 'teaching', 'competition'
 * @param firebaseType Training type from Firebase
 * @returns Compatible training type for UI components
 */
export const mapTrainingType = (firebaseType: string): TrainingSessionData['type'] => {
  const typeMap: Record<string, TrainingSessionData['type']> = {
    training: 'training',
    seminar: 'seminar',
    camp: 'camp',
    teaching: 'teaching',
    competition: 'competition'
  };

  return typeMap[firebaseType] || 'training'; // Default to 'training'
};

/**
 * Maps UI training types back to Firebase training types
 * @param uiType Training type from UI
 * @returns Compatible training type for Firebase
 */
export const mapSessionTypeToFirebase = (uiType: TrainingSessionData['type']): string => {
  const typeMap: Record<string, string> = {
    gi: 'training',        // gi maps to training in Firebase
    nogi: 'nogi',
    openmat: 'openmat',
    drilling: 'drilling',
    private: 'teaching',   // private maps to teaching in Firebase
    competition: 'competition',
    seminar: 'seminar',
    other: 'other'
  };
  
  return typeMap[uiType] || 'other';
};

/**
 * Groups training sessions by date for calendar views
 * @param sessions Array of training sessions
 * @returns Object with date strings as keys and arrays of sessions as values
 */
export const groupSessionsByDate = (
  sessions: TrainingSessionData[]
): Record<string, TrainingSessionData[]> => {
  return sessions.reduce((groups, session) => {
    // Format date as YYYY-MM-DD for grouping using LOCAL timezone
    const dateObj = typeof session.date === 'string' ? new Date(session.date) : session.date;
    
    // Use local date formatting instead of UTC to avoid timezone shifts
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    
    // Initialize array if needed
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    
    // Add session to appropriate date group
    groups[dateKey].push(session);
    
    return groups;
  }, {} as Record<string, TrainingSessionData[]>);
};

/**
 * Transforms training data into a format suitable for calendar heat maps
 * @param sessions Array of training sessions
 * @returns Array of {date, count, value} objects for heat maps
 */
export const transformSessionsForCalendar = (
  sessions: TrainingSessionData[]
): Array<{ date: string; count: number; value: number }> => {
  // Group sessions by date
  const groupedSessions = groupSessionsByDate(sessions);
  
  // Transform to calendar format
  return Object.entries(groupedSessions).map(([date, dateSessions]) => {

    
    return {
      date,
      count: dateSessions.length,
      value: dateSessions.length // Use count as value for heat intensity
    };
  });
};

/**
 * Returns training session status information for display
 * @param session Training session data
 * @returns Status object with color and text
 */
export const getTrainingSessionStatus = (
  session: TrainingSessionData
): { color: string; text: string } => {
  if (session._offline) {
    return { color: 'gray', text: 'Offline' };
  }
  
  if (session._pendingSync) {
    return { color: 'amber', text: 'Syncing' };
  }
  
  const sessionDate = typeof session.date === 'string' ? new Date(session.date) : session.date;
  const now = new Date();
  
  // Future session
  if (sessionDate > now) {
    return { color: 'blue', text: 'Upcoming' };
  }
  
  // Recent session (last 24 hours)
  if (now.getTime() - sessionDate.getTime() < 24 * 60 * 60 * 1000) {
    return { color: 'green', text: 'Recent' };
  }
  
  // Regular session
  return { color: 'gray', text: 'Completed' };
};

const exportedObject = {
  adaptFirebaseTrainingToSessionData,
  adaptSessionDataToFirebaseTraining,
  mapTrainingType,
  mapSessionTypeToFirebase,
  groupSessionsByDate,
  transformSessionsForCalendar,
  getTrainingSessionStatus
};

export default exportedObject;